import { apiClient } from './client';

export interface Vehicle {
  id: number; vehicle_reference: string; registration_number: string; make: string | null; model: string | null; year: number | null;
  vehicle_type: string; ownership_type: string; duty_station: string | null; assigned_driver_employee_id: number | null;
  fuel_type: string; current_odometer: string; insurance_expiry: string | null; registration_expiry: string | null;
  roadworthiness_expiry: string | null; next_service_date: string | null; status: string; notes: string | null;
}

export interface VehicleRequest {
  id: number; request_reference: string; requester_employee_id: number; purpose: string; pickup_location: string; destination: string;
  departure_date: string; expected_return_date: string; number_of_passengers: number | null; priority: string;
  assigned_vehicle_id: number | null; assigned_driver_employee_id: number | null; status: string;
}

export interface VehicleDispatch {
  id: number; dispatch_reference: string; vehicle_id: number; driver_employee_id: number; destination: string; purpose: string;
  departure_datetime: string; return_datetime: string | null; opening_odometer: string; closing_odometer: string | null; status: string;
}

export interface Driver {
  id: number; employee_id: number; driver_name: string; licence_number: string; licence_expiry_date: string;
  defensive_driving_certified: number; status: string;
}

export interface FuelTransaction {
  id: number; transaction_type: string; station_vendor: string | null; quantity_litres: string; unit_cost: string | null;
  total_cost: string | null; transaction_date: string; is_flagged_for_review: number;
}

export interface VehicleMaintenance {
  id: number; maintenance_reference: string; maintenance_type: string; issue_description: string; request_date: string;
  workshop_vendor: string | null; estimated_cost: string | null; actual_cost: string | null; status: string;
}

export const vehiclesApi = {
  list: (filters: { status?: string } = {}) => {
    const params = new URLSearchParams(Object.entries(filters).filter(([, v]) => v) as [string, string][]);
    return apiClient.get<{ data: Vehicle[] }>(`/api/vehicles${params.toString() ? `?${params}` : ''}`);
  },
  get: (id: number) => apiClient.get<{ data: Vehicle }>(`/api/vehicles/${id}`),
  create: (input: Record<string, unknown>) => apiClient.post<{ data: { id: number; vehicleReference: string } }>('/api/vehicles', input),
  setStatus: (id: number, status: string) => apiClient.put<{ data: { id: number; status: string } }>(`/api/vehicles/${id}/status`, { status }),

  listDispatches: (vehicleId: number) => apiClient.get<{ data: VehicleDispatch[] }>(`/api/vehicles/${vehicleId}/dispatches`),
  listFuel: (vehicleId: number) => apiClient.get<{ data: FuelTransaction[] }>(`/api/vehicles/${vehicleId}/fuel`),
  getFuelConsumption: (vehicleId: number) => apiClient.get<{ data: { total_litres: string | null; total_cost: string | null; transaction_count: number } }>(`/api/vehicles/${vehicleId}/fuel/consumption`),
  listMaintenance: (vehicleId: number) => apiClient.get<{ data: VehicleMaintenance[] }>(`/api/vehicles/${vehicleId}/maintenance`),
};

export const vehicleRequestsApi = {
  list: (filters: { status?: string } = {}) => {
    const params = new URLSearchParams(Object.entries(filters).filter(([, v]) => v) as [string, string][]);
    return apiClient.get<{ data: VehicleRequest[] }>(`/api/vehicle-requests${params.toString() ? `?${params}` : ''}`);
  },
  get: (id: number) => apiClient.get<{ data: VehicleRequest }>(`/api/vehicle-requests/${id}`),
  create: (input: Record<string, unknown>) => apiClient.post<{ data: { id: number; requestReference: string } }>('/api/vehicle-requests', input),
  submit: (id: number) => apiClient.post<{ data: { id: number; status: string } }>(`/api/vehicle-requests/${id}/submit`, {}),
  approve: (id: number) => apiClient.post<{ data: { id: number; status: string } }>(`/api/vehicle-requests/${id}/approve`, {}),
  reject: (id: number, reason: string) => apiClient.post<{ data: { id: number; status: string } }>(`/api/vehicle-requests/${id}/reject`, { reason }),
  assign: (id: number, input: { vehicleId: number; driverEmployeeId: number }) => apiClient.post<{ data: { id: number } }>(`/api/vehicle-requests/${id}/assign`, input),
};

export const vehicleDispatchApi = {
  create: (input: Record<string, unknown>) => apiClient.post<{ data: { id: number; dispatchReference: string } }>('/api/vehicle-dispatches', input),
  recordReturn: (id: number, input: Record<string, unknown>) => apiClient.post<{ data: { id: number; status: string; kilometresTravelled: number } }>(`/api/vehicle-dispatches/${id}/return`, input),
};

export const driversApi = {
  list: (filters: { status?: string } = {}) => {
    const params = new URLSearchParams(Object.entries(filters).filter(([, v]) => v) as [string, string][]);
    return apiClient.get<{ data: Driver[] }>(`/api/drivers${params.toString() ? `?${params}` : ''}`);
  },
  get: (id: number) => apiClient.get<{ data: Driver }>(`/api/drivers/${id}`),
  create: (input: Record<string, unknown>) => apiClient.post<{ data: { id: number } }>('/api/drivers', input),
  setStatus: (id: number, status: string) => apiClient.put<{ data: { id: number; status: string } }>(`/api/drivers/${id}/status`, { status }),
  listExpiringLicences: () => apiClient.get<{ data: Driver[] }>('/api/drivers/expiring-licences'),
};

export const fuelApi = {
  create: (input: Record<string, unknown>) => apiClient.post<{ data: { id: number; transactionReference: string } }>('/api/fuel-transactions', input),
  flag: (id: number, notes: string) => apiClient.post<{ data: { id: number; flagged: boolean } }>(`/api/fuel-transactions/${id}/flag`, { notes }),
};

export const maintenanceApi = {
  create: (input: Record<string, unknown>) => apiClient.post<{ data: { id: number; maintenanceReference: string } }>('/api/vehicle-maintenance', input),
  approve: (id: number, approvedCost: number) => apiClient.post<{ data: { id: number; status: string } }>(`/api/vehicle-maintenance/${id}/approve`, { approvedCost }),
  start: (id: number) => apiClient.post<{ data: { id: number; status: string } }>(`/api/vehicle-maintenance/${id}/start`, {}),
  complete: (id: number, input: Record<string, unknown>) => apiClient.post<{ data: { id: number; status: string } }>(`/api/vehicle-maintenance/${id}/complete`, input),
};
